import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";

/* v3.2 · Reliability graphical editor (RBD — Reliability Block Diagram).
   Nodes model an availability path from SOURCE → components → SINK.
   Each component carries its own λ (failure rate) and MTTR; nodes flagged
   `parallel` represent a redundant/backup path. Edges are visual only —
   the backend consumes the flat list of components and their tag/config
   fields.

   Payload: { components: [{ tag, failure_rate_per_yr, mttr_hours,
                             configuration: "series" | "parallel" }],
              customers } */

const Block   = (p) => <BusNode {...p} {...NODE_STYLE.BLOCK}
  sub={(d) => `λ=${d.failure_rate_per_yr}/yr · MTTR ${d.mttr_hours}h · ${d.configuration}`} />;
const Source  = (p) => <BusNode {...p} {...NODE_STYLE.SOURCE}
  sub={(d) => `feed · ${d.customers} customers`} />;
const Sink    = (p) => <BusNode {...p} {...NODE_STYLE.SINK}
  sub={() => "load"} />;

const nodeTypes = { BLOCK: Block, SOURCE: Source, SINK: Sink };

const INITIAL_NODES = [
  { id: "SRC", type: "SOURCE", position: { x: 40, y: 200 },
    data: { id: "SRC", tag: "Utility", customers: 250 } },
  { id: "C1", type: "BLOCK", position: { x: 240, y: 140 },
    data: { id: "C1", tag: "Utility feeder", failure_rate_per_yr: 0.02,
             mttr_hours: 6, configuration: "series" } },
  { id: "C2", type: "BLOCK", position: { x: 440, y: 140 },
    data: { id: "C2", tag: "MV breaker", failure_rate_per_yr: 0.005,
             mttr_hours: 4, configuration: "series" } },
  { id: "C3", type: "BLOCK", position: { x: 640, y: 140 },
    data: { id: "C3", tag: "Transformer", failure_rate_per_yr: 0.008,
             mttr_hours: 24, configuration: "series" } },
  { id: "SINK", type: "SINK", position: { x: 860, y: 200 },
    data: { id: "SINK", tag: "Feeder load" } },
];
const INITIAL_EDGES = [
  { id: "e1", source: "SRC", target: "C1", ...edgeStyle() },
  { id: "e2", source: "C1", target: "C2", ...edgeStyle() },
  { id: "e3", source: "C2", target: "C3", ...edgeStyle() },
  { id: "e4", source: "C3", target: "SINK", ...edgeStyle() },
];

const FIELDS = {
  SOURCE: [
    { key: "tag",       label: "Tag",                type: "text" },
    { key: "customers", label: "Customers on feeder", type: "number" },
  ],
  BLOCK: [
    { key: "tag",                 label: "Component tag",       type: "text" },
    { key: "failure_rate_per_yr", label: "Failure rate λ / yr", type: "number", step: "0.001" },
    { key: "mttr_hours",          label: "MTTR (hours)",        type: "number", step: "0.5" },
    { key: "configuration",       label: "Configuration",       type: "select",
      options: [{ value: "series", label: "series (in-line)" },
                { value: "parallel", label: "parallel (redundant)" }] },
  ],
  SINK: [
    { key: "tag", label: "Tag", type: "text" },
  ],
};

/* Serialise the graph into the /api/studies/reliability payload. */
export function graphToReliability(nodes) {
  const src = nodes.find((n) => n.type === "SOURCE")?.data || {};
  const blocks = nodes
    .filter((n) => n.type === "BLOCK")
    .map((n) => ({
      tag:                 n.data.tag || n.data.id,
      failure_rate_per_yr: Number(n.data.failure_rate_per_yr || 0.01),
      mttr_hours:          Number(n.data.mttr_hours || 4),
      configuration:       (n.data.configuration || "series").toLowerCase(),
    }));
  return {
    components: blocks,
    customers:  Number(src.customers || 100),
  };
}

function ReliabilitySLDInner({ onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(10);

  useEffect(() => { onChange?.(graphToReliability(nodes)); }, [nodes, onChange]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, id: `e${idCounter.current++}`, ...edgeStyle() }, es));
  }, [setEdges]);

  const addNode = (type) => {
    const id = `${type[0]}${idCounter.current++}`;
    const defaults = type === "BLOCK"
      ? { tag: id, failure_rate_per_yr: 0.01, mttr_hours: 4, configuration: "series" }
      : type === "SOURCE"
      ? { tag: "Utility", customers: 100 }
      : { tag: "Load" };
    setNodes((ns) => [...ns, {
      id, type,
      position: { x: 120 + Math.random() * 500, y: 60 + Math.random() * 260 },
      data: { id, ...defaults },
    }]);
  };

  const addParallelBlock = () => {
    // Convenience: drop a `parallel` block directly under the currently
    // selected block, or below the last block if nothing is selected.
    const anchor = (sel.node && sel.node.type === "BLOCK")
      ? sel.node
      : [...nodes].reverse().find((n) => n.type === "BLOCK");
    const id = `P${idCounter.current++}`;
    const pos = anchor
      ? { x: anchor.position.x, y: anchor.position.y + 120 }
      : { x: 300, y: 320 };
    setNodes((ns) => [...ns, {
      id, type: "BLOCK", position: pos,
      data: { id, tag: id, failure_rate_per_yr: 0.01, mttr_hours: 4,
              configuration: "parallel" },
    }]);
  };

  const clearAll = () => { setNodes([]); setEdges([]); setSel({ node: null, edge: null }); };
  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) =>
    setSel({ node: ns[0] || null, edge: es[0] || null }), []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const deleteSel = () => {
    if (sel.node) {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      const id = sel.edge.id;
      setEdges((es) => es.filter((e) => e.id !== id));
    }
    setSel({ node: null, edge: null });
  };

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="rel-sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#00E5FF" onClick={() => addNode("BLOCK")}
          testid="rel-add-block" title="Add series component">
          <Plus size={10} className="inline" /> series
        </ToolbarBtn>
        <ToolbarBtn colour="#7CFF6B" onClick={addParallelBlock}
          testid="rel-add-parallel" title="Add parallel (redundant) block">
          <Plus size={10} className="inline" /> parallel
        </ToolbarBtn>
        <ToolbarBtn colour="#FFCC00" onClick={() => addNode("SOURCE")}
          testid="rel-add-source" title="Add source">
          <Plus size={10} className="inline" /> source
        </ToolbarBtn>
        <ToolbarBtn colour="#FF9F0A" onClick={() => addNode("SINK")}
          testid="rel-add-sink" title="Add sink / load">
          <Plus size={10} className="inline" /> sink
        </ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel}
          testid="rel-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll}
          testid="rel-clear" title="Reset">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
      </ReactFlow>

      {sel.node && (
        <SidePanel
          title={sel.node.data.tag || sel.node.data.id}
          subtitle={sel.node.type.toLowerCase()}
          node={sel.node}
          fields={FIELDS[sel.node.type] || []}
          onChange={patchNode}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function ReliabilitySLD({ onChange }) {
  return (
    <ReactFlowProvider>
      <ReliabilitySLDInner onChange={onChange} />
    </ReactFlowProvider>
  );
}
